import { Avatar, PlayerIcon, SpecGlyph, type RhClass } from '@/components/events/EventDetailPanel'
import type { SignUp } from '@/components/events/types'
import { useLanguage } from '@/contexts/LanguageContext'

/**
 * Who signed up, as one board rather than a row that scrolls sideways.
 *
 * Every playing position gets a cell, empty ones included: on a match page the
 * gaps are the point — an empty Recon column is what makes someone fill it. The
 * positions that mean "not playing" are not columns at all; they sit in one
 * quiet strip at the bottom, because they answer a different question.
 */
export default function SignUpBoard({ signUps, classes }: {
  signUps: SignUp[]
  classes: RhClass[]
}) {
  const { T } = useLanguage()

  const groups: Record<string, SignUp[]> = {}
  for (const su of signUps) (groups[su.class_name] ??= []).push(su)

  const known = new Set(classes.map(c => c.name))
  const emojiOf = (name: string) => classes.find(c => c.name === name)?.emoji
  const specOf = (su: SignUp) => su.spec_name
    ? classes.find(c => c.name === su.class_name)?.specs.find(s => s.name === su.spec_name)
      ?? { name: su.spec_name }
    : null

  // Positions in the order Settings lists them, then anything a sign-up still
  // names that Settings no longer has.
  const playing = classes.filter(c => c.kind === 'primary' || c.kind === undefined)
  const bench = classes.filter(c => c.kind === 'bench')
  const out = classes.filter(c => c.kind === 'tentative' || c.kind === 'out')
  const removed = Object.keys(groups).filter(name => !known.has(name))

  const columns = [
    ...playing.map(c => ({ name: c.name, emoji: c.emoji, dim: false, removed: false })),
    ...bench.map(c => ({ name: c.name, emoji: c.emoji, dim: true, removed: false })),
    ...removed.map(name => ({ name, emoji: emojiOf(name), dim: true, removed: true })),
  ].filter(col => !col.removed ? true : groups[col.name]?.length)

  const notPlaying = out
    .map(c => ({ name: c.name, emoji: c.emoji, players: groups[c.name] ?? [] }))
    .filter(g => g.players.length > 0)

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5">
        {columns.map(col => {
          const players = groups[col.name] ?? []
          return (
            <section
              key={col.name}
              className={`rounded-sm border border-white/10 bg-white/[0.025] p-2.5 ${col.dim ? 'opacity-75' : ''}`}
            >
              <header className="mb-2 flex items-center gap-1.5 border-b border-white/10 pb-1.5">
                <PlayerIcon su={{ class_name: col.name, class_emote_id: null, spec_emote_id: null }} emoji={col.emoji} />
                <h4 className="min-w-0 flex-1 truncate text-[11px] font-semibold tracking-wider text-muted-foreground">
                  {col.name}
                </h4>
                {col.removed && (
                  <span className="shrink-0 text-[9px] italic text-muted-foreground/60" title={T.eventDetail.removedClassHint}>
                    {T.eventDetail.removedClass}
                  </span>
                )}
                <span className="shrink-0 text-[11px] tabular-nums text-muted-foreground/60">{players.length}</span>
              </header>

              {players.length === 0 ? (
                <p className="py-1.5 text-center text-[11px] text-muted-foreground/35">{T.matches.positionEmpty}</p>
              ) : (
                <ul className="space-y-1">
                  {players.map(su => (
                    <li key={su.user_id} className="flex items-center gap-1.5 rounded bg-white/[0.04] px-1.5 py-1">
                      <Avatar su={su} px={18} />
                      {(() => {
                        // The sub-class's icon right before the name; its name
                        // after it only when it has no icon.
                        const spec = specOf(su)
                        const icon = !!spec && !!(spec.emoji || spec.emoteId)
                        return (
                          <>
                            {icon && <span className="flex shrink-0" title={spec.name}><SpecGlyph spec={spec} size={14} /></span>}
                            <span className="min-w-0 truncate text-[11px]">{su.name}</span>
                            {spec && !icon && <span className="shrink-0 truncate text-[10px] text-muted-foreground/70">· {spec.name}</span>}
                          </>
                        )
                      })()}
                    </li>
                  ))}
                </ul>
              )}
            </section>
          )
        })}
      </div>

      {notPlaying.length > 0 && (
        <div className="rounded-sm border border-dashed border-white/10 px-3 py-2.5">
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
            {notPlaying.map(g => (
              <div key={g.name} className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
                <span className="flex shrink-0 items-center gap-1.5 text-[11px] text-muted-foreground/70">
                  <PlayerIcon su={{ class_name: g.name, class_emote_id: null, spec_emote_id: null }} emoji={g.emoji} />
                  {g.name}
                </span>
                {g.players.map(su => (
                  <span key={su.user_id} className="flex items-center gap-1 rounded bg-white/[0.04] px-1.5 py-0.5">
                    <Avatar su={su} px={14} />
                    <span className="max-w-32 truncate text-[11px] text-muted-foreground">{su.name}</span>
                  </span>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
